<script>
import { TheMask } from 'vue-the-mask'

import { apiSecond } from '@/boot/axios'

import PageAlert from '@/components/UI/molecules/PageAlert.vue'
import TextInput from '@/components/components/Inputs/TextInput.vue'
import CalendarPicker from '@/components/UI/CalendarPicker.vue'
import SelectPool from '@/components/catalog/SelectPool.vue'
import SelectRegionList from '@/components/catalog/SelectRegionList.vue'
import SelectDistrict from '@/components/catalog/SelectDistrict.vue'
import SelectReservoirsList from '@/components/catalog/SelectReservoirsList.vue'
import SelectPlots from '@/components/catalog/SelectPlots.vue'
import SelectStatus from '@/components/catalog/SelectStatus.vue'
import SelectOrgType from '@/components/catalog/SelectOrgType'

export default {
	name: 'DocumentsItem',
	components: {
		TheMask,
		PageAlert,
		TextInput,
		CalendarPicker,
		SelectPool,
		SelectRegionList,
		SelectDistrict,
		SelectReservoirsList,
		SelectPlots,
		SelectStatus,
		SelectOrgType,
	},
	props: {
		statusPage: {
			type: String,
			default: 'create',
		},
	},
	data() {
		return {
			loading: false,
			form: {
				iin_bin: '',
				name: '',
				start_at: null,
				finish_at: null,
				catch_type: null,
				pool: null,
				region: null,
				district: null,
				reservoir: null,
				plot: null,
				document: null,
				status: null,
			},
			fileUrl: '',
			documentRequiredError: false,
			// Подсветку незаполненных обязательных полей показываем только после попытки сохранить
			showRequired: false,
			application: null,
			errorMessage: {
				status: null,
				messages: null,
				text: null,
			},
		}
	},
	computed: {
		getReportTitle() {
			if (this.statusPage == 'show') return this.$t('sidebar.documents')
			else if (this.statusPage == 'edit') return this.$t('sidebar.documents')
			return this.$t('sidebar.documents')
		},
		isNotEditable() {
			return this.statusPage == 'show'
		},
		isFullFilled() {
			return this.statusPage === 'edit' || this.statusPage === 'show'
		},
		isCanEdit() {
			return (item) => {
				if ([3].includes(this.$store.state.userObject.role_id)) {
					return ['rejected'].includes(item?.status)
				} else if ([12].includes(this.$store.state.userObject.role_id)) {
					return true
				}
				return false
			}
		},
		isAdmin() {
			return [12].includes(this.$store.state.userObject.role_id)
		},
		// Те же поля, что бэкенд требует при сохранении (422)
		missingRequired() {
			const required = [
				'name',
				'start_at',
				'finish_at',
				'catch_type',
				'pool',
				'region',
				'district',
				'reservoir',
			]
			return required.filter((key) => {
				const value = this.form[key]
				return typeof value === 'string' ? !value.trim() : !value
			})
		},
		requiredErrors() {
			return (key) => {
				const isError = this.showRequired && this.missingRequired.includes(key)
				// Формат, который ожидают TextInput и Select*: { $error, required }
				return { $error: isError, required: !isError }
			}
		},
		validationMessage() {
			const messages = this.errorMessage.messages
			if (!messages) return null
			const text =
				typeof messages === 'string' ? messages : Object.values(messages).flat().join(' ')
			return { status: this.errorMessage.status, text }
		},
	},
	watch: {
		'form.region': {
			handler(newVal, oldVal) {
				if (newVal?.id !== oldVal?.id) {
					this.form.district = null
					if (newVal && newVal.id) {
						this.$store.dispatch('_fetchDistricts', { regions: [newVal.id] })
					}
				}
			},
			deep: true,
		},
		'form.district': {
			handler(newVal, oldVal) {
				if (newVal?.id !== oldVal?.id) {
					// Clear reservoir when district changes
					this.form.reservoir = null
					if (newVal && newVal.id && this.form.pool?.id) {
						this.$store.dispatch('_fetchPondList', {
							pools: [this.form.pool.id],
							districts: [newVal.id],
						})
					}
				}
			},
			deep: true,
		},
	},
	beforeMount() {
		this.apiGetUserDocumentList()
	},
	beforeCreate() {
		if (!localStorage.token) this.$router.push('/ru/login')
	},
	methods: {
		async onSubmit() {
			try {
				this.loading = true
				this.showRequired = true
				// File required check - allow existing documents when editing
				if (!this.form.document) {
					this.errorMessage.text = this.$t('messages.file_required')
					this.documentRequiredError = true
				}
				if (this.missingRequired.length || !this.form.document) {
					this.$toast.warning(this.$t('toast.fill_required'))
					this.loading = false
					return
				}

				const formData = new FormData()
				formData.append('name', this.form.name)
				if (this.form.iin_bin) formData.append('iin_bin', this.form.iin_bin.replaceAll(' ', ''))
				if (this.form.start_at) formData.append('start_at', this.form.start_at)
				if (this.form.finish_at) formData.append('finish_at', this.form.finish_at)
				if (this.form.catch_type) formData.append('catch_type_id', this.form.catch_type?.id)
				if (this.form.pool) formData.append('pool_id', this.form.pool?.id)
				if (this.form.region) formData.append('region_id', this.form.region?.id)
				if (this.form.status) formData.append('status', this.form.status?.id)
				if (this.form.district) formData.append('district_id', this.form.district?.id)
				if (this.form.reservoir) formData.append('reservoir_id', this.form.reservoir?.id)
				if (this.form.plot) formData.append('plot_id', this.form.plot?.id)

				// Only append document if it's a new file (not an existing one)
				if (this.form.document && !this.form.document.existing) {
					formData.append('document', this.form.document)
				}

				const response = await apiSecond.post(
					this.isFullFilled
						? `v1/user/document/${this.$route.params.id}`
						: 'v1/user/document/create',
					formData,
					{
						headers: {
							'Content-Type': 'multipart/form-data',
						},
					},
				)

				if (response.data.data) {
					this.$toast.success(this.$t(this.isFullFilled ? 'toast.saved' : 'toast.created'))
					if (this.isFullFilled) {
						this.$router.push(`/${this.$i18n.locale}/account/documents/${response.data.data.id}`)
					} else {
						this.$router.push(`/${this.$i18n.locale}/account/documents/`)
					}
				}
			} catch (error) {
				this.catchError(error)
				if (![401, 403, 422, 500].includes(error?.response?.status)) {
					this.$toast.apiError(error, this.$t('toast.save_error'))
				}
			} finally {
				this.loading = false
			}
		},
		async parseServerData(data) {
			if (!this.$store.getters._getOrgType || this.$store.getters._getOrgType.length === 0) {
				await this.$store.dispatch('_fetchOrgType')
			}

			const orgTypes = this.$store.getters._getOrgType
			const locale = this.$i18n.locale
			const orgType = orgTypes.find((t) => t.id === data.org_type_id)
			let orgTypeDisplay = null
			if (orgType) {
				orgTypeDisplay = {
					...orgType,
					title: locale === 'kz' ? orgType.title_kaz : orgType.title,
				}
			}

			this.form = {
				name: data.name,
				iin_bin: data.iin_bin,
				start_at: data.start_at,
				finish_at: data.finish_at,
				catch_type: orgTypeDisplay,
				pool: data.pool,
				region: data.region,
				district: null, // set after districts are loaded
				reservoir: data.reservoir,
				plot: data.plot,
				document: data.document ? { existing: true } : null,
			}
			if (data.document) this.fileUrl = `https://api.efish.kz${data.document}`

			// Fetch districts for the selected region, then set district
			if (data.region && data.region.id) {
				await this.$store.dispatch('_fetchDistricts', { regions: [data.region.id] })
				this.form.district = data.district
			}

			// Fetch ponds (reservoirs) for the selected pool and district
			if (data.pool && data.pool.id && data.district && data.district.id) {
				await this.$store.dispatch('_fetchPondList', {
					pools: [data.pool.id],
					districts: [data.district.id],
				})
			}

			// Set reservoir after all data is loaded to ensure it's not cleared by component updates
			this.$nextTick(() => {
				this.form.reservoir = data.reservoir
			})
		},
		async apiGetUserDocumentList() {
			if (!this.isFullFilled) return
			try {
				this.loading = true
				const response = await apiSecond.get('v1/user/document/' + this.$route.params.id)
				if (response) {
					this.application = response.data
					this.parseServerData(response.data)
				}
			} catch (error) {
				this.catchError(error)
			} finally {
				this.loading = false
			}
		},
		catchError(error) {
			if (error?.response?.status == 500) {
				this.errorMessage.status = 500
				this.errorMessage.text = this.$t('system_message.500')
			}
			if (error?.response?.status == 401) {
				this.errorMessage.status = 401
				this.errorMessage.text = error.response.data.error_messages
				this.$router.push('/' + this.$i18n.locale + '/login')
			}
			if (error?.response?.status == 422) {
				this.errorMessage.status = 422
				this.errorMessage.messages = error.response.data.error_messages
			}
			if (error?.response?.status == 403) {
				this.errorMessage.status = 403
				this.errorMessage.text = error?.response?.data?.message
			}
		},
		handleFileUpload() {
			this.errorMessage = {
				status: null,
				text: null,
				messages: null,
			}
			this.documentRequiredError = false
			const file = this.$refs.fileDocuments.files[0]
			if (!file) {
				this.errorMessage.text = this.$t('messages.file_required')
				return
			}
			if (file.size > 20 * 1000 * 1000) {
				this.errorMessage.text = this.$t('messages.file_to_big', ['20 Mb'])
				return
			}
			// Replace existing document with new file
			this.form.document = file
			this.fileUrl = URL.createObjectURL(this.form.document)
		},
	},
	head: {
		title() {
			return {
				inner: this.$t('sidebar.users_parent'),
			}
		},
		meta: [],
	},
}
</script>

<template>
	<div class="wrap__content">
		<div class="container">
			<div class="row">
				<div class="col-xl-2 col-lg-3">
					<v-sidebar :active="['DocumentManagement']"></v-sidebar>
				</div>
				<div class="col-xl-10 col-lg-9">
					<div class="content-wrapper">
						<div class="card__content">
							<div class="card__content-header">
								<div class="content__title">
									<div class="content__page--back">
										<a @click="$router.go(-1)">
											<svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M13.125 5.25l-5.25 5.25 5.25 5.25" stroke="#52A5FC" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
										</a>
									</div>
									<div class="content__title--element">
										<div class="content__title--text">
											{{ $t('sidebar.documents') }}
										</div>
										<div v-if="application?.name" class="content__title--subtext">
											{{ application.name }}
										</div>
									</div>
								</div>
							</div>
							<PageAlert
								v-if="application?.comment"
								:text="`${$t('messages.reject_reason', [application.comment])}`"
								:type="'error'"
								class="mb-[15px]"
							/>
							<div class="card__content--body">
								<div v-if="isAdmin && !isFullFilled" class="form__line">
									<div class="form__line--left">
										<div class="form__line--title">{{ $t('headers.iin_bin') }}</div>
									</div>
									<div class="form__line--right">
										<div class="row">
											<div class="col-xl-5">
												<div
													class="form__line--input"
													:class="[
														errorMessage.messages && errorMessage.messages.iin_bin
															? 'input--error'
															: '',
													]"
												>
													<TheMask
														v-model="form.iin_bin"
														:masked="true"
														:name="'iin_bin'"
														:placeholder="$t('headers.iin_bin')"
														:mask="['### ### ### ###']"
														:disabled="isNotEditable"
														type="text"
														class="input-linck"
													/>
												</div>
												<div
													class="input-required"
													v-if="errorMessage.messages && errorMessage.messages.iin_bin"
												>
													<p
														v-for="(massage, massageInd) in errorMessage.messages.iin_bin"
														:key="massageInd"
													>
														{{ massage }}
													</p>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div class="form__line">
									<div class="form__line--left">
										<div class="form__line--title">
											{{ $t('labels.name') }} <span class="form__line--required">*</span>
										</div>
									</div>
									<div class="form__line--right">
										<div class="row">
											<div class="col-xl-5">
												<TextInput
													:required="true"
													:disabled="isNotEditable"
													:modelValue="form.name"
													:errors="requiredErrors('name')"
													v-on:update:modelValue="form.name = $event"
													:placeholder="$t('placeholder.name')"
												></TextInput>
											</div>
										</div>
									</div>
								</div>
								<div class="form__line">
									<div class="form__line--left">
										<div class="form__line--title">
											{{ $t('labels.start_at') }} <span class="form__line--required">*</span>
										</div>
									</div>
									<div class="form__line--right">
										<div class="row">
											<div class="col-xl-5">
												<div
													class="form__line--input"
													:class="{ 'input--error': requiredErrors('start_at').$error }"
												>
													<CalendarPicker
														:required="true"
														:disabled="isNotEditable"
														:value="form.start_at"
														v-model="form.start_at"
													/>
												</div>
												<div v-if="requiredErrors('start_at').$error" class="input-required">
													{{ $t('system_message.required_message') }}
												</div>
											</div>
										</div>
									</div>
								</div>
								<div class="form__line">
									<div class="form__line--left">
										<div class="form__line--title">
											{{ $t('labels.finish_at') }} <span class="form__line--required">*</span>
										</div>
									</div>
									<div class="form__line--right">
										<div class="row">
											<div class="col-xl-5">
												<div
													class="form__line--input"
													:class="{ 'input--error': requiredErrors('finish_at').$error }"
												>
													<CalendarPicker
														:required="true"
														:disabled="isNotEditable"
														:value="form.finish_at"
														v-model="form.finish_at"
													/>
												</div>
												<div v-if="requiredErrors('finish_at').$error" class="input-required">
													{{ $t('system_message.required_message') }}
												</div>
											</div>
										</div>
									</div>
								</div>
								<div class="form__line">
									<div class="form__line--left">
										<div class="form__line--title">
											{{ $t('headers.adding_doc') }} <span class="form__line--required">*</span
											><br />
											(PDF)
										</div>
									</div>
									<div class="form__line--right">
										<div class="col-xl-7">
											<div
												class="form__line--input"
												:class="[
													documentRequiredError ||
													(errorMessage.messages && errorMessage.messages.document)
														? 'input--error'
														: '',
												]"
											>
												<label
													:class="{ 'add_tourist--success': form.document }"
													class="form__line--file"
												>
													<input
														type="file"
														ref="fileDocuments"
														name="fileDocuments"
														accept=".pdf"
														v-on:change="handleFileUpload()"
													/>
													<div v-if="form.document" class="form__file">
														<div class="form__file--text">
															{{
																form.document.existing
																	? $t('messages.upload_new_document')
																	: form.document.name
															}}
														</div>
														<div class="form__file--icon">
															<svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M14.167 11.475a3.188 3.188 0 00-1.771-5.808H11.12a4.958 4.958 0 10-8.288 4.887M8.5 8.5v6.375M5.667 12.042L8.5 14.875l2.833-2.833" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
														</div>
													</div>
													<div v-else class="form__file">
														<div class="form__file--text">{{ $t('site.upload_file') }}</div>
														<div class="form__file--icon">
															<svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M14.167 11.475a3.188 3.188 0 00-1.771-5.808H11.12a4.958 4.958 0 10-8.288 4.887M8.5 8.5v6.375M5.667 12.042L8.5 14.875l2.833-2.833" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
														</div>
													</div>
												</label>
											</div>
											<a
												v-if="fileUrl"
												:href="fileUrl"
												target="_blank"
												class="mt-1 italic text-blue-500 text-xs"
											>
												{{ $t(`template_text.view`) }}
											</a>
											<div v-if="documentRequiredError" class="input-required">
												<p>{{ $t('messages.file_required') }}</p>
											</div>
											<p
												v-if="form.document && form.document.existing"
												class="mt-1 text-xs text-gray-600"
											>
												{{ $t('messages.existing_document_upload_new') }}
											</p>
											<div
												class="input-required"
												v-if="errorMessage.messages && errorMessage.messages.document"
											>
												<p
													v-for="(massage, massageInd) in errorMessage.messages.document"
													:key="massageInd"
												>
													{{ massage }}
												</p>
											</div>
										</div>
									</div>
								</div>
								<div class="form__line">
									<div class="form__line--left">
										<div class="form__line--title">
											{{ $t('headers.org_type') }} <span class="form__line--required">*</span>
										</div>
									</div>
									<div class="form__line--right">
										<div class="row">
											<div class="col-xl-5">
												<div
													class="quota-fish__item"
													:class="[
														errorMessage.messages && errorMessage.messages.type_catch_id
															? 'input--error'
															: '',
													]"
												>
													<div class="row align-items-center">
														<div class="col-xl-12">
															<SelectOrgType
																:value="form.catch_type"
																:required="true"
																:errors="requiredErrors('catch_type')"
																:disabled="isNotEditable"
																:with-title="false"
																:multiple="false"
																@input="form.catch_type = $event"
																:placeholder="$t('placeholder.select_catch_type')"
															></SelectOrgType>
														</div>
													</div>
												</div>
												<div
													class="input-required"
													v-if="errorMessage.messages && errorMessage.messages.type_catch_id"
												>
													<p>{{ errorMessage.messages.type_catch_id }}</p>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div class="form__line">
									<div class="form__line--left">
										<div class="form__line--title">
											{{ $t('handbooks.Бассейны') }} <span class="form__line--required">*</span>
										</div>
									</div>
									<div class="form__line--right">
										<div class="row">
											<div class="col-xl-5">
												<div class="form__line--input">
													<SelectPool
														:value="form.pool"
														:required="true"
														:errors="requiredErrors('pool')"
														:disabled="isNotEditable"
														:with-title="false"
														:multiple="false"
														@input="form.pool = $event"
														:placeholder="$t('placeholder.select_pond')"
													/>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div class="form__line">
									<div class="form__line--left">
										<div class="form__line--title">
											{{ $t('headers.quota_region') }} <span class="form__line--required">*</span>
										</div>
									</div>
									<div class="form__line--right">
										<div class="row">
											<div class="col-xl-5">
												<div class="form__line--input">
													<SelectRegionList
														:value="form.region"
														:required="true"
														:errors="requiredErrors('region')"
														:disabled="isNotEditable"
														:filters="{
															pools: form.pool?.id ? [form.pool?.id] : null,
														}"
														:watch-values="[form.pool]"
														:with-title="false"
														:multiple="false"
														@input="form.region = $event"
														:placeholder="$t('placeholder.select_region')"
													/>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div class="form__line">
									<div class="form__line--left">
										<div class="form__line--title">
											{{ $t('handbooks.Районы') }} <span class="form__line--required">*</span>
										</div>
									</div>
									<div class="form__line--right">
										<div class="row">
											<div class="col-xl-5">
												<div class="form__line--input">
													<SelectDistrict
														:value="form.district"
														:required="true"
														:errors="requiredErrors('district')"
														:disabled="isNotEditable"
														:filters="{
															regions: form.region?.id ? [form.region?.id] : null,
														}"
														:watch-values="[form.region]"
														:with-title="false"
														:multiple="false"
														:confinesValue="$store.getters._getDistricts"
														@input="form.district = $event"
														:placeholder="$t('placeholder.select_district')"
													/>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div class="form__line">
									<div class="form__line--left">
										<div class="form__line--title">
											{{ $t('headers.pond') }} <span class="form__line--required">*</span>
										</div>
									</div>
									<div class="form__line--right">
										<div class="row">
											<div class="col-xl-5">
												<div class="form__line--input">
													<SelectReservoirsList
														:value="form.reservoir"
														:required="true"
														:errors="requiredErrors('reservoir')"
														:disabled="isNotEditable"
														:filters="{
															pools: form.pool?.id ? [form.pool?.id] : null,
															districts: form.district?.id ? [form.district?.id] : null,
														}"
														:watch-values="[]"
														:with-title="false"
														:multiple="false"
														:placeholder="$t('placeholder.select_reservoir')"
														:confinesValue="$store.state.catalogs.ponds"
														@input="form.reservoir = $event"
													/>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div class="form__line">
									<div class="form__line--left">
										<div class="form__line--title">{{ $t('handbooks.Участки') }}</div>
									</div>
									<div class="form__line--right">
										<div class="row">
											<div class="col-xl-5">
												<div class="form__line--input">
													<p class="text-xs italic mb-1">
														{{ $t('messages.if_exist_in_document') }}
													</p>
													<SelectPlots
														:value="form.plot"
														:required="false"
														:disabled="isNotEditable"
														:filters="{
															pools: form.pool?.id ? [form.pool?.id] : null,
															regions: form.region?.id ? [form.region?.id] : null,
															districts: form.district?.id ? [form.district?.id] : null,
															ponds: form.reservoir?.id ? [form.reservoir?.id] : null,
														}"
														:watch-values="[form.pool, form.district, form.reservoir]"
														:with-title="false"
														:multiple="false"
														@input="form.plot = $event"
														:placeholder="$t('placeholder.select_plot')"
													/>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div v-if="isAdmin" class="form__line">
									<div class="form__line--left">
										<div class="form__line--title">{{ $t('headers.status') }}</div>
									</div>
									<div class="form__line--right">
										<div class="row">
											<div class="col-xl-5">
												<div class="form__line--input">
													<SelectStatus
														:value="form.status"
														:with-title="false"
														:multiple="false"
														:disabled="isNotEditable"
														:confinesValue="$t(`statuses.aggrement`)"
														@input="form.status = $event"
													/>
												</div>
											</div>
										</div>
									</div>
								</div>

								<template>
									<hr class="reporting__hr" />

									<div class="flex items-center gap-4">
										<button
											v-if="!isNotEditable"
											type="submit"
											class="input-btn form__line--btn"
											:disabled="loading"
											@click="onSubmit"
										>
											{{ isFullFilled ? $t('commands.update') : $t('button.save') }}
										</button>
										<router-link
											v-if="isNotEditable && isCanEdit(application)"
											:to="`/${$i18n.locale}/account/documents/${application?.id}/edit`"
											class="input-btn form__line--btn form__line--btn-black"
										>
											{{ $t('button.edit') }}
										</router-link>
									</div>
								</template>
								<v-preloader v-if="validationMessage" :message="validationMessage"></v-preloader>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div v-if="errorMessage.text" class="alert alert-danger" style="margin-top: 20px">
			{{ errorMessage.text }}
		</div>
		<!-- <div style="color: red; font-size: 12px">
			Debug: districts: {{ $store.getters._getDistricts }}
		</div> -->
		<!-- <div style="color: blue; font-size: 12px; margin-top: 20px">
			Debug: Ponds in store: {{ $store.state.catalogs.ponds?.length || 0 }}, Form reservoir:
			{{ form.reservoir ? `${form.reservoir.id} - ${form.reservoir.title}` : 'null' }}
		</div> -->
	</div>
</template>

<style scoped>
.form__line--required {
	color: #dc3545;
}

/* Глобальный .input--error красит только input, у multiselect рамка на .multiselect__tags */
.card__content--body ::v-deep .input--error .multiselect__tags {
	border-color: #f3574d;
}
</style>
